import type { Post, PostCategory } from '../../../types';
import { normalizePostCategory } from '../../../types';
import { suppressPairedBefores } from '../../../utils/postPairing';
import { resolveAfterImage, resolvePair } from '../../../utils/beforeAfter';

/**
 * Slide selection and ordering for the desktop map's observation
 * presentation. Pure — no React, no Firebase, no store reads — so it is
 * unit-testable and callable straight from Map.web.tsx's open handler.
 *
 * Moved here from map/slideshowOrder.ts when the presentation was split
 * into a controller, a stage and two surfaces.
 */

export interface SlideshowBounds {
  south: number;
  west: number;
  north: number;
  east: number;
}

/** Synchronous centroid lookup for a structure-linked post with no own
 *  location — Map.web.tsx wires this to the structures store's in-memory
 *  shard cache. Returns null when the structure isn't loaded. */
export type StructureCentroidLookup = (
  structureId: string
) => { latitude: number; longitude: number } | null;

export type SlideCategory = Exclude<PostCategory, 'news'>;

/**
 * The ordered URL ladder for one photo on a slide. `display` is what the
 * stage finally shows; `thumbnail` is the cheap 400px variant it paints
 * first, both as the blurred backdrop and as the sharp image's placeholder,
 * so the stage is never blank while the ~500KB display file is in flight.
 *
 * `thumbnail` is absent when the post has none (pairs that predate the
 * Resize Images extension) or when it would be the same file as `display`.
 */
export interface SlideImage {
  thumbnail?: string;
  display: string;
}

export interface Slide {
  kind: 'single' | 'pair';
  post: Post;
  /** Main/"after" photo. */
  image: SlideImage;
  /** "Before" photo, pair slides only. */
  beforeImage?: SlideImage;
  category: SlideCategory;
  caption: string;
  addressText?: string;
  createdAt: Post['createdAt'];
}

/** A narrative break inserted before each category group in a deck — see
 *  buildDeck. Carries no photo, so it needs no counter slot. */
export interface TitleCard {
  kind: 'title';
  category: SlideCategory;
  count: number;
}

export type DeckItem = Slide | TitleCard;

// Every session pays for prefetching + holding these in memory during a
// Zoom presentation, and a slideshow with hundreds of slides stops being a
// presentation. 60 is generous headroom over what a single zoomed-in area
// realistically holds.
const SLIDESHOW_CAP = 60;

function locationFor(
  post: Post,
  structureCentroid: StructureCentroidLookup
): { latitude: number; longitude: number } | null {
  if (post.location) {
    return { latitude: post.location.latitude, longitude: post.location.longitude };
  }
  if (post.structureId) {
    return structureCentroid(post.structureId);
  }
  return null;
}

function inBounds(
  location: { latitude: number; longitude: number },
  bounds: SlideshowBounds
): boolean {
  return (
    location.latitude <= bounds.north &&
    location.latitude >= bounds.south &&
    location.longitude >= bounds.west &&
    location.longitude <= bounds.east
  );
}

/** Pairs a display URL with its thumbnail, dropping the thumbnail when it
 *  is missing or is the very file the stage is already loading. */
function ladder(display: string | undefined, thumbnail: string | undefined): SlideImage | null {
  if (!display) return null;
  return thumbnail && thumbnail !== display ? { display, thumbnail } : { display };
}

export function selectSlides({
  posts,
  bounds,
  structureCentroid,
}: {
  posts: Post[];
  bounds: SlideshowBounds;
  structureCentroid: StructureCentroidLookup;
}): Slide[] {
  // News is text-first and carries no reliable photo — excluded up front so
  // it never counts toward pairing or the cap.
  const observations = posts.filter((post) => normalizePostCategory(post.category) !== 'news');

  // knownPosts is the full unfiltered list (not `observations`) so a before
  // post gets suppressed even if the after that paired it were somehow
  // excluded above — matches the suppressPairedBefores contract used
  // elsewhere (feed, structure threads).
  const deduped = suppressPairedBefores(observations, posts);

  const slides: Slide[] = [];
  for (const post of deduped) {
    const location = locationFor(post, structureCentroid);
    if (!location || !inBounds(location, bounds)) continue;

    const pair = resolvePair(post, true);
    let kind: Slide['kind'];
    let image: SlideImage | null;
    let beforeImage: SlideImage | null = null;
    if (pair) {
      kind = 'pair';
      image = ladder(post.displayUrl ?? pair.afterUri, pair.afterPlaceholder ?? post.thumbnailUrl);
      beforeImage = ladder(pair.beforeUri, pair.beforePlaceholder);
    } else {
      kind = 'single';
      image = ladder(post.displayUrl ?? resolveAfterImage(post, true), post.thumbnailUrl);
    }
    // Photoless — dropped rather than shown blank.
    if (!image) continue;

    slides.push({
      kind,
      post,
      image,
      beforeImage: beforeImage ?? undefined,
      category: normalizePostCategory(post.category) as SlideCategory,
      caption: post.caption,
      addressText: post.address,
      createdAt: post.createdAt,
    });
  }

  const newestFirst = (a: Slide, b: Slide) => b.createdAt.toMillis() - a.createdAt.toMillis();
  const progress = slides.filter((s) => s.category === 'progress').sort(newestFirst);
  const risk = slides.filter((s) => s.category !== 'progress').sort(newestFirst);

  // Split the cap between the two groups. Cutting after one sort (progress
  // first) dropped the whole opportunities half in a busy view: at zoom 15
  // over Polique Canyon, progress alone held 78 slides (Sept 25 2026). Each
  // group gets half, and room one group doesn't use goes to the other.
  const half = Math.floor(SLIDESHOW_CAP / 2);
  const riskTake = Math.min(risk.length, Math.max(half, SLIDESHOW_CAP - progress.length));
  const progressTake = Math.min(progress.length, SLIDESHOW_CAP - riskTake);
  return [...progress.slice(0, progressTake), ...risk.slice(0, riskTake)];
}

/**
 * Inserts a title card ahead of each run of same-category slides — a
 * narrative break between the progress ("green") half of a presentation and
 * the opportunity ("red") half, so a red photo never lands with no warning
 * right after a run of green ones. `selectSlides` already sorts every
 * 'progress' slide before every 'risk' slide, so in practice this yields at
 * most two title cards, but the grouping here is general (any number of
 * contiguous same-category runs) so it doesn't depend on that ordering.
 */
export function buildDeck(slides: Slide[]): DeckItem[] {
  const deck: DeckItem[] = [];
  let i = 0;
  while (i < slides.length) {
    const category = slides[i].category;
    let count = 0;
    while (i + count < slides.length && slides[i + count].category === category) {
      count++;
    }
    deck.push({ kind: 'title', category, count });
    for (let j = 0; j < count; j++) {
      deck.push(slides[i + j]);
    }
    i += count;
  }
  return deck;
}

/**
 * Photo-only slide numbering ("3 of 14") for a deck: title cards don't count
 * and get no number. Built once per deck rather than scanned on every render
 * of the counter.
 */
export function photoPositions(deck: DeckItem[]): {
  numberByIndex: Map<number, number>;
  total: number;
} {
  const numberByIndex = new Map<number, number>();
  let total = 0;
  deck.forEach((item, i) => {
    if (item.kind !== 'title') {
      total += 1;
      numberByIndex.set(i, total);
    }
  });
  return { numberByIndex, total };
}

/** Every display URL a deck item renders — one for a single, two for a
 *  pair, none for a title card. Used by the controller's prefetch. */
export function displayUrlsFor(item: DeckItem): string[] {
  if (item.kind === 'title') return [];
  const urls = [item.image.display];
  if (item.beforeImage) urls.push(item.beforeImage.display);
  return urls;
}

/**
 * A deck, described by the two things that decide whether it is still the
 * right deck for what the map is showing: the bounds it was built from and
 * how many photo slides came out.
 */
export interface DeckView {
  bounds: SlideshowBounds;
  photoCount: number;
}

/** How much of the smaller of two boxes their overlap covers, 0 to 1. */
export function boundsOverlapRatio(a: SlideshowBounds, b: SlideshowBounds): number {
  const areaOf = (x: SlideshowBounds) =>
    Math.max(0, x.north - x.south) * Math.max(0, x.east - x.west);
  const areaA = areaOf(a);
  const areaB = areaOf(b);
  // A zero-height or zero-width box has no area to share, so the only
  // sensible reading of "the same view" is exact equality.
  if (areaA <= 0 || areaB <= 0) {
    return a.south === b.south && a.north === b.north && a.west === b.west && a.east === b.east
      ? 1
      : 0;
  }
  const overlapLat = Math.min(a.north, b.north) - Math.max(a.south, b.south);
  const overlapLng = Math.min(a.east, b.east) - Math.max(a.west, b.west);
  if (overlapLat <= 0 || overlapLng <= 0) return 0;
  return (overlapLat * overlapLng) / Math.min(areaA, areaB);
}

/** Default for viewDiffers: the overlap has to cover this much of the
 *  smaller box before the two views count as the same view. */
export const VIEW_TOLERANCE = 0.85;

/**
 * Would a deck built from `current` differ from the one already presented?
 * Two things make it differ: a different count of photo slides, or bounds
 * that have moved off the presented ones.
 *
 * The geometry test measures the overlap against the SMALLER of the two
 * boxes on purpose. Zooming in without losing an observation leaves the
 * deck's contents identical (the new box is inside the old one and holds
 * the same slides), and offering to rebuild an identical deck is noise. A
 * zoom that does drop or add observations changes the count, which is the
 * other half of the test.
 *
 * Pure: no map, no store. `presented` is null before anything is presented,
 * and then nothing has moved.
 */
export function viewDiffers(
  presented: DeckView | null,
  current: DeckView,
  tolerance: number = VIEW_TOLERANCE
): boolean {
  if (!presented) return false;
  if (presented.photoCount !== current.photoCount) return true;
  return boundsOverlapRatio(presented.bounds, current.bounds) < tolerance;
}
